/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { getDefaultImpl } from "cms/components/components/component";
import { useFieldZone } from "cms/fields/hooks/use-field-zone";
import { useCurrentDynamicField } from "cms/fields/hooks/use-current-dynamic-field";
import { store } from "data/store/index";
import { useGetCurrentOrganization } from "hooks/useGetCurrentOrganization";
import { addComponent, removeComponent, selectCurrentModel, setComponents } from "data/store/reducers/model/index";
import { getNamedComponentsFromModel } from "hooks/useModelNamedComponentsField";
import { omit } from "lodash";
import { useDispatch } from "react-redux";
import { checkDiffs, cleanUnwantedProperties, prepareChangeData } from "utils/change-detection";
import { generateGuid } from "utils/empty-exists";
import { Logger, yellowBright } from "cms/utils/log/logger";

const useContentZone = function () {

    const organization = useGetCurrentOrganization();
    const { field, position: fieldPosition } = useCurrentDynamicField();
    const { applyFieldChanges } = useFieldZone();
    const dispatch = useDispatch();

    const onChange = (components) => {
        const update = {
            ...field,
            values: [{ value: components, settings: null }],
        };
        const updatedField = {
            ...field,
            ...update,
        };
        const { original, modified } = prepareChangeData(field, updatedField);
        const isDirty = checkDiffs(original, modified, "[component-field] handleOnChange for applyFieldChanges");
        if (isDirty) {
            applyFieldChanges(original, modified);
        }
    };

    const handleUpdateComponent = async (fieldName, component) => {
        Logger.debug(yellowBright("ComponentZone handleUpdateComponent"));
        // temporary hack to prevent cached props
        const model = selectCurrentModel(store.getState());
        const components = getNamedComponentsFromModel(model, fieldName);
        // remove known properties that should not be diff checked or saved
        const updatedComponent = cleanUnwantedProperties(component);
        let updatedComponents = [];
        if (components) {
            updatedComponents = components.map((comp) => {
                return (comp?.guid === component?.guid)
                    ? updatedComponent
                    : comp;
            });
        } else {
            updatedComponents.push(updatedComponent);
        }
        onChange(updatedComponents);
    };

    const addComponentToFieldAndUpdateModel = async (fieldName, component, componentIndex) => {
        if (!component) {
            throw Error("Component was undefined in: useContentZone -> addComponentToFieldAndUpdateModel");
        } else if (!fieldName) {
            throw Error("FieldName was undefined in: useContentZone -> addComponentToFieldAndUpdateModel");
        } else {
            const { type } = component;
            Logger.debug(yellowBright`ComponentZone addComponentToFieldAndUpdateModel [${type}]`);
            // This code runs within a specific field.component
            // ...it adds the component to the components list for that field
            dispatch(addComponent({
                fieldPosition,
                component,
                componentIndex
            }));
        }
    };

    const handleDeleteComponent = async (fieldName, component) => {
        Logger.debug(yellowBright("ComponentZone handleDeleteComponent"), { component });

        dispatch(removeComponent({
            fieldPosition,
            guid: component.guid
        }));
    };

    const handleUpdateComponents = async (fieldName, updatedComponents) => {
        Logger.debug(yellowBright("ComponentZone handleUpdateComponents"), { updatedComponents });

        dispatch(setComponents({
            fieldPosition,
            components: updatedComponents
        }));
    };

    const getComponentPosition = (components, componentIndex) => {
        if (!components || components.length == 0) {
            return 1;
        }
        if (componentIndex == null) {
            return components.length + 1;
        }
        return componentIndex + 1;
    };

    const handleSelectComponentForReuse = (fieldName, actionType, component, label, componentIndex) => {
        Logger.debug(yellowBright("ComponentZone handleSelectComponentForReuse"));
        const model = selectCurrentModel(store.getState());
        const components = getNamedComponentsFromModel(model, fieldName);
        const componentPosition = getComponentPosition(components, componentIndex);
        let newComponent = {
            ...component,
            position: componentPosition,
        };
        if (actionType === "duplicate") {
            newComponent.guid = generateGuid();
        }
        if (actionType === "reuse") {
            newComponent.label = label;
        }
        newComponent = omit(newComponent, ["component","sample"]);
        addComponentToFieldAndUpdateModel(fieldName, newComponent, componentIndex);
        return newComponent;
    };

    const handleSelectComponentType = (fieldName, type, componentIndex) => {
        Logger.debug(yellowBright("ComponentZone handleSelectComponentType"));
        if (!type) {
            throw Error("type was undefined in: useContentZone -> handleSelectComponentType");
        }

        const model = selectCurrentModel(store.getState());
        const components = getNamedComponentsFromModel(model, fieldName);
        const componentPosition = getComponentPosition(components, componentIndex);
        const defaultImpl = getDefaultImpl(type);
        const newComponent = {
            ...defaultImpl,
            guid: generateGuid(),
            position: componentPosition,
            organization: organization.guid,
        };

        addComponentToFieldAndUpdateModel(fieldName, newComponent, componentIndex);
        return newComponent;
    };

    return {
        handleSelectComponentForReuse,
        handleSelectComponentType,
        handleUpdateComponent,
        handleUpdateComponents,
        handleDeleteComponent,
    };

};

export { useContentZone };
